import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type DividerLayout = 'horizontal' | 'vertical';
export type DividerType = 'solid' | 'dashed' | 'dotted';
export type DividerAlign = 'left' | 'center' | 'right' | 'top' | 'bottom';

/**
 * Divider component for separating content.
 * Forked from legacy libraries and restyled with Razor/Tailwind design system.
 *
 * GAP-FE-021 · migrated from decorator-based @Input to signal-based input().
 */
@Component({
  selector: 'ui-divider',
  standalone: true,
  imports: [],
  templateUrl: './divider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'role': 'separator',
    '[attr.aria-orientation]': 'layout()',
    '[class]': 'hostClasses()'
  }
})
export class DividerComponent {
  /** Specifies the orientation of the divider. */
  readonly layout = input<DividerLayout>('horizontal');

  /** Border style type. */
  readonly type = input<DividerType>('solid');

  /** Alignment of the content. */
  readonly align = input<DividerAlign>('center');

  /** Additional CSS classes to apply. */
  readonly styleClass = input<string>('');

  /** Set by content projection detection (preserved for template compatibility). */
  readonly hasContent = false;

  readonly hostClasses = computed<string>(() => {
    const base = ['ui-divider', 'flex', 'items-center'];

    if (this.layout() === 'vertical') {
      base.push('flex-col', 'w-px', 'min-h-full', 'mx-4');
    } else {
      base.push('w-full', 'my-4');
    }

    const typeClasses: Record<DividerType, string> = {
      solid: 'border-solid',
      dashed: 'border-dashed',
      dotted: 'border-dotted'
    };
    base.push(typeClasses[this.type()]);
    base.push('border-bb-border');

    if (this.layout() === 'vertical') {
      base.push('border-l');
    } else {
      base.push('border-t');
    }

    const sc = this.styleClass();
    if (sc) base.push(sc);

    return base.join(' ');
  });

  readonly contentClasses = computed<string>(() => {
    const base = ['px-3', 'text-bb-text-secondary', 'text-sm', 'bg-bb-bg-secondary'];

    if (this.layout() === 'horizontal') {
      const alignClasses: Record<string, string> = {
        left: 'mr-auto',
        center: 'mx-auto',
        right: 'ml-auto'
      };
      const mapped = alignClasses[this.align()];
      if (mapped) base.push(mapped);
    }

    if (this.layout() === 'vertical') {
      const alignClasses: Record<string, string> = {
        top: 'mb-auto',
        center: 'my-auto',
        bottom: 'mt-auto'
      };
      const mapped = alignClasses[this.align()];
      if (mapped) base.push(mapped);
    }

    return base.join(' ');
  });
}
